import React, { useState } from 'react'
import { Upload, X, Image as ImageIcon, Loader2 } from 'lucide-react'
import { EventFormData } from '../EventCreationWizard'
import { supabase } from '../../../lib/supabase'
import LoadingSpinner from '../../ui/LoadingSpinner'
import toast from 'react-hot-toast'
import { EnterpriseToast } from '../../ui/EnterpriseToast'
import { useIsMounted } from '../../../hooks/useIsMounted'
import { withTimeout } from '../../../utils/promise'

interface EventDetailsStepProps {
  formData: EventFormData
  updateFormData: (updates: Partial<EventFormData>) => void
  onNext: () => void
  onPrev: () => void
  isFirstStep: boolean
  isLastStep: boolean
}

const EventDetailsStep: React.FC<EventDetailsStepProps> = ({
  formData,
  updateFormData
}) => {
  const [uploading, setUploading] = useState(false)
  const [imagePreview, setImagePreview] = useState<string | undefined>(formData.image_url)
  const [isDragging, setIsDragging] = useState(false)
  const [isRemoving, setIsRemoving] = useState(false)
  const isMounted = useIsMounted()

  // Sync imagePreview with formData.image_url when it changes
  React.useEffect(() => {
    setImagePreview(formData.image_url)
  }, [formData.image_url])

  const uploadFile = async (file: File) => {
    console.log('[EventDetailsStep] Starting upload:', file.name, file.type, file.size)

    // Validate file type
    if (!file.type.startsWith('image/')) {
      console.log('[EventDetailsStep] Invalid file type')
      toast.custom((t) => (
        <EnterpriseToast
          t={t}
          type="error"
          title="Invalid File Type"
          message="Please upload an image file"
        />
      ))
      return
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      console.log('[EventDetailsStep] File too large')
      toast.custom((t) => (
        <EnterpriseToast
          t={t}
          type="error"
          title="File Too Large"
          message="Image must be less than 5MB"
        />
      ))
      return
    }

    // Delete old image if replacing
    if (formData.image_url) {
      console.log('[EventDetailsStep] Removing existing image before upload')
      await handleRemoveImage()
    }

    console.log('[EventDetailsStep] Setting uploading state to true')
    setUploading(true)
    try {
      // Create unique filename
      const fileExt = file.name.split('.').pop()
      const fileName = `${Math.random().toString(36).substring(2)}-${Date.now()}.${fileExt}`
      const filePath = `event-images/${fileName}`

      // Upload to Supabase Storage
      console.log('[EventDetailsStep] Uploading to Supabase:', filePath)
      const { error: uploadError } = await supabase.storage
        .from('secret-assets')
        .upload(filePath, file, {
          cacheControl: '3600',
          upsert: false
        })

      if (uploadError) {
        console.error('[EventDetailsStep] Upload error:', uploadError)
        throw uploadError
      }

      // Get public URL
      console.log('[EventDetailsStep] Getting public URL')
      const { data: { publicUrl } } = supabase.storage
        .from('secret-assets')
        .getPublicUrl(filePath)

      console.log('[EventDetailsStep] Upload successful, URL:', publicUrl)
      setImagePreview(publicUrl)
      updateFormData({ image_url: publicUrl })
      toast.custom((t) => (
        <EnterpriseToast
          t={t}
          type="success"
          title="Upload Complete"
          message="Event image uploaded successfully"
        />
      ))
    } catch (error) {
      console.error('[EventDetailsStep] Upload failed:', error)
      toast.custom((t) => (
        <EnterpriseToast
          t={t}
          type="error"
          title="Upload Failed"
          message="Failed to upload image. Please try again."
        />
      ))
    } finally {
      console.log('[EventDetailsStep] Setting uploading state to false')
      setUploading(false)
    }
  }

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    await uploadFile(file)
  }

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragging(true)
  }

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragging(false)
  }

  const handleDrop = async (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragging(false)

    const file = e.dataTransfer.files?.[0]
    if (!file) return
    await uploadFile(file)
  }

  const handleRemoveImage = async () => {
    if (!formData.image_url) return;

    // Set loading state
    setIsRemoving(true);
    console.log('[EventDetailsStep] Starting image removal for:', formData.image_url);

    try {
      // Step 1: Extract the file path from the public URL.
      const url = new URL(formData.image_url);
      const pathParts = url.pathname.split('/storage/v1/object/public/secret-assets/');
      if (pathParts.length <= 1) {
        throw new Error('Invalid image URL format. Cannot extract file path.');
      }
      const filePath = decodeURIComponent(pathParts[1]);

      // Step 2: Perform the storage deletion with a 10-second timeout.
      console.log('[EventDetailsStep] Deleting file from Supabase Storage:', filePath);
      const { error: deleteError } = await withTimeout(
        supabase.storage.from('secret-assets').remove([filePath]),
        10000,
        'Supabase storage deletion timed out.'
      );

      if (deleteError) {
        // If the deletion itself fails, throw an error.
        throw new Error(`Storage deletion failed: ${deleteError.message}`);
      }

      console.log('[EventDetailsStep] File deleted successfully from storage.');

      // Step 3: ONLY update state if the component is still mounted.
      if (isMounted.current) {
        console.log('[EventDetailsStep] Component is mounted. Clearing UI state.');
        setImagePreview(undefined);
        updateFormData({ image_url: undefined });
        toast.custom((t) => (
          <EnterpriseToast
            t={t}
            type="success"
            title="Image Removed"
            message="Event image has been removed"
          />
        ))
      } else {
        console.log('[EventDetailsStep] Component unmounted. Skipping UI state update.');
      }

    } catch (error) {
      console.error('[EventDetailsStep] Image removal failed:', error);
      // Provide user feedback regardless of mounted state, as toast is global.
      toast.custom((t) => (
        <EnterpriseToast
          t={t}
          type="error"
          title="Removal Failed"
          message="Failed to remove image. Please try again."
        />
      ))
    } finally {
      // Step 4: ALWAYS clear the loading state if the component is still mounted.
      if (isMounted.current) {
        console.log('[EventDetailsStep] Clearing loading state.');
        setIsRemoving(false);
      }
    }
  }

  const titleLength = formData.title.length

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-foreground mb-2">Event Details</h2>
        <p className="text-muted-foreground">
          Start by adding the essential information about your event
        </p>
      </div>

      {/* Event Title */}
      <div>
        <label className="block text-sm font-medium text-foreground mb-2">
          Event Title <span className="text-destructive">*</span>
        </label>
        <input
          type="text"
          value={formData.title}
          onChange={(e) => updateFormData({ title: e.target.value })}
          placeholder="e.g., SECRET Lobster Pop-Up - Downtown LA"
          maxLength={100}
          className="w-full px-4 py-3 bg-background border border-input rounded-md text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
        />
        <div className="flex items-center justify-between mt-1">
          <p className="text-xs text-muted-foreground">
            This will be displayed to customers
          </p>
          <p className={`text-xs ${titleLength > 90 ? 'text-orange-500' : 'text-muted-foreground'}`}>
            {titleLength}/100
          </p>
        </div>
      </div>

      {/* Event Description */}
      <div>
        <label className="block text-sm font-medium text-foreground mb-2">
          Event Description
        </label>
        <textarea
          value={formData.description}
          onChange={(e) => updateFormData({ description: e.target.value })}
          placeholder="Describe your event... What makes it special? What should customers expect?"
          rows={6}
          className="w-full px-4 py-3 bg-background border border-input rounded-md text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent resize-none"
        />
        <p className="text-xs text-muted-foreground mt-1">
          Optional. Add details about the event, menu highlights, or special instructions.
        </p>
      </div>

      {/* Event Image */}
      <div>
        <label className="block text-sm font-medium text-foreground mb-2">
          Event Image <span className="text-destructive">*</span>
        </label>

        {imagePreview ? (
          <div className="relative rounded-lg overflow-hidden border-2 border-border group">
            <img
              src={imagePreview}
              alt="Event preview"
              className="w-full h-64 object-cover"
            />
            <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
              <button
                type="button"
                onClick={handleRemoveImage}
                disabled={isRemoving}
                className="flex items-center space-x-2 px-4 py-2 bg-destructive text-white rounded-md hover:bg-destructive/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isRemoving ? (
                  <>
                    <LoadingSpinner size="sm" />
                    <span>Removing...</span>
                  </>
                ) : (
                  <>
                    <X size={16} />
                    <span>Remove Image</span>
                  </>
                )}
              </button>
            </div>
            <div className="absolute top-2 right-2 bg-green-500 text-white text-xs px-2 py-1 rounded">
              ✓ Uploaded
            </div>
          </div>
        ) : (
          <label className="block">
            <div
              className={`relative border-2 border-dashed rounded-lg p-12 text-center cursor-pointer transition-all min-h-[250px] ${
                isDragging
                  ? 'border-primary bg-primary/10'
                  : 'border-border hover:border-primary hover:bg-muted/50'
              }`}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
            >
              {/* Default content - always rendered to maintain container size */}
              {!uploading && (
                <div className="flex flex-col items-center space-y-3">
                  <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center">
                    <ImageIcon className="text-primary" size={32} />
                  </div>
                  <div>
                    <p className="text-foreground font-medium mb-1">
                      Click to upload or drag and drop
                    </p>
                    <p className="text-sm text-muted-foreground">
                      PNG, JPG or WebP (max 5MB)
                    </p>
                    <p className="text-xs text-muted-foreground mt-2">
                      Recommended: 1600x900px (16:9 ratio)
                    </p>
                  </div>
                </div>
              )}

              {/* Loading overlay - shown when uploading */}
              {uploading && (
                <div
                  className="absolute inset-0 flex flex-col items-center justify-center rounded-lg"
                  style={{ backgroundColor: 'rgba(0, 0, 0, 0.7)' }}
                >
                  <Loader2
                    className="animate-spin"
                    style={{ width: '64px', height: '64px', color: '#ffffff' }}
                  />
                  <p
                    style={{
                      marginTop: '16px',
                      fontSize: '18px',
                      fontWeight: '500',
                      color: '#ffffff',
                      WebkitTextFillColor: '#ffffff'
                    }}
                  >
                    Uploading image...
                  </p>
                </div>
              )}
            </div>
            <input
              type="file"
              accept="image/*"
              onChange={handleImageUpload}
              disabled={uploading}
              className="hidden"
            />
          </label>
        )}
        <p className="text-xs text-muted-foreground mt-2">
          This image will be the hero image for your event card
        </p>
      </div>
    </div>
  )
}

export default EventDetailsStep
